import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Radio, Search, RefreshCw, Activity, Trophy, SatelliteDish, Info, Zap, ArrowUpRight, ChevronDown } from "lucide-react";
import { PageHeader } from "@/components/shell/TopBar";
import { LiveMatchCard } from "@/components/cards/LiveMatchCard";
import { LiveLocked } from "@/components/cards/LiveLocked";
import { MarketBadge, CompetitionLabel, RiskChip, TeamMono } from "@/components/badges/Badges";
import { TeamLogo, logoFrom, resolveTeamVisual } from "@/components/common/TeamLogo";
import { LoadingCards, ErrorState, EmptyState } from "@/components/common/States";
import { useLive, useLiveNow, useMarketHub } from "@/hooks/useAtlas";
import { useSession } from "@/context/SessionContext";
import { useLaunch } from "@/context/LaunchContext";
import { resolveMarket } from "@/presentation/marketRegistry";
import { lifecycleOf } from "@/presentation/resultPresentation";
import { canonicalLiveEventKeys, inCanonicalLiveEvent } from "@/presentation/entitlements";
import { fixtureKeys } from "@/presentation/fixtureIdentity";
import { isLiveNow } from "@/presentation/liveState";
import { normalizeCompetition } from "@/presentation/competitionNormalizer";
import { fmtNum, fmtPct, scoreLine } from "@/lib/format";
import { LiveSignal, LiveNowFixture } from "@/types/api";
import { useI18n } from "@/i18n";

function MonitorStat({ icon, value, label, tone }: { icon: React.ReactNode; value: React.ReactNode; label: string; tone: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3">
      <span className={`grid h-9 w-9 place-items-center rounded-lg ${tone}`}>{icon}</span>
      <div><p className="num text-lg font-bold text-foreground">{value}</p><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p></div>
    </div>
  );
}

function SectionHeader({ icon, title, count, tone }: { icon: React.ReactNode; title: string; count: number; tone: string }) {
  return (
    <div className="mb-3 mt-8 flex items-center gap-2.5 first:mt-0">
      <span className={`grid h-7 w-7 place-items-center rounded-lg ${tone}`}>{icon}</span>
      <h2 className="text-base font-bold tracking-tight text-foreground md:text-lg">{title}</h2>
      <span className="num rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-xs font-semibold text-muted-foreground">{count}</span>
    </div>
  );
}

// Authoritative LIVE NOW card: real score/minute from the live feed; ATLAS pick
// shown only when a signal is joined by exact fixture_id (no fabrication).
function LiveNowCard({ f, signal, index = 0 }: { f: LiveNowFixture; signal: LiveSignal | null; index?: number }) {
  const { d, lang } = useI18n();
  // Team names must always be visible next to the crest. Tolerant resolution +
  // a last-resort label so a name never disappears even if the feed omits it.
  const homeName = f.home_team || (lang === "tr" ? "Ev Sahibi" : "Home");
  const awayName = f.away_team || (lang === "tr" ? "Deplasman" : "Away");
  return (
    <div className="panel panel-hover group relative overflow-hidden p-4" data-testid="live-match-card"
      style={{ animationDelay: `${Math.min(index * 40, 300)}ms` }}>
      <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-live/10 blur-3xl" />
      <div className="flex items-start justify-between gap-2">
        <CompetitionLabel country={f.country} league={f.league} leagueLogo={logoFrom(f, "league")} />
        <span className="num inline-flex items-center gap-1 rounded-md bg-live/10 px-2 py-0.5 text-[11px] font-bold text-live-400">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-live" />
          {f.minute != null ? `${f.minute}'` : (f.status || (lang === "tr" ? "Canlı" : "Live"))}
        </span>
      </div>

      {signal ? (
        <div className="mt-4 rounded-xl border border-atlas/20 bg-atlas/[0.05] p-3">
          <p className="eyebrow mb-1.5 text-atlas-300">{lang === "tr" ? "ATLAS seçimi" : "ATLAS pick"}</p>
          <div className="flex items-center justify-between gap-2">
            <MarketBadge code={signal.market_code} backendLabel={signal.market_name} />
            <RiskChip code={signal.market_code} />
          </div>
          {signal.model_probability != null && <p className="mt-2 text-[11px] text-muted-foreground">{d.common.confidence}: <span className="num text-atlas-300">{fmtPct(signal.model_probability)}</span></p>}
        </div>
      ) : (
        <div className="mt-4 rounded-xl border border-white/8 bg-white/[0.02] p-3" data-testid="live-now-no-signal">
          <p className="text-[11px] text-muted-foreground">{lang === "tr" ? "ATLAS bu maçta şu anda aktif sinyal üretmedi." : "ATLAS has no active signal on this match yet."}</p>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-center gap-2"><TeamLogo src={resolveTeamVisual(f.home_team, "home", [f])} name={homeName} size={24} /><p className="truncate text-sm font-semibold text-foreground" data-testid="live-now-home-name">{homeName}</p></div>
          <div className="flex items-center gap-2"><TeamLogo src={resolveTeamVisual(f.away_team, "away", [f])} name={awayName} size={24} /><p className="truncate text-sm font-semibold text-foreground" data-testid="live-now-away-name">{awayName}</p></div>
        </div>
        <div className="shrink-0 text-right">
          <div className="num text-2xl font-bold tracking-tight text-foreground">{scoreLine(f.home_goals, f.away_goals)}</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{d.common.score}</div>
        </div>
      </div>

      <div className="mt-4 flex items-center">
        <Link to={`/app/match/${f.fixture_id}`} data-testid="live-now-detail-link"
          className="ml-auto inline-flex items-center gap-1 rounded-full border border-white/5 bg-white/[0.02] px-3 py-1.5 text-[11px] font-semibold text-muted-foreground transition hover:border-atlas/30 hover:text-atlas-300">
          {lang === "tr" ? "Maç Merkezi" : "Match Center"} <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}

export default function Live() {
  const { d, lang } = useI18n();
  const { isAuthenticated } = useSession();
  // LIVE is a backend entitlement (app:live:full → PRO+). FREE gets a premium
  // locked product view and never requests the protected LIVE feeds.
  const canLive = useLaunch().hasPlan("PRO");
  const hubView = useMarketHub().data;
  const { data, isLoading, isError, error, refetch, isFetching } = useLive(120);
  const liveNowQ = useLiveNow(isAuthenticated && canLive);
  const [market, setMarket] = useState<string>("ALL");
  const [q, setQ] = useState("");
  const [poolOpen, setPoolOpen] = useState(false);

  const rows = data?.rows || [];
  // Authoritative live feed is used when authenticated and it returned data.
  const useAuthFeed = isAuthenticated && !liveNowQ.isError && Array.isArray(liveNowQ.data);
  // CANONICAL CUSTOMER-LIVE OWNERSHIP (EVENT-LEVEL): Market Hub `live` is the
  // authoritative set. A signal from the legacy/raw /api/live feed may render
  // ONLY if the SAME production event (event id, else fixture+market+minute,
  // else fixture+market) exists canonically — never fixture_id alone, so a stale
  // event on a fixture can't ride in on another canonical event. Falls back to
  // the raw feed only when the canonical set is unavailable (unauth/preview/error).
  const canonicalLiveKeys = useMemo(() => canonicalLiveEventKeys(hubView?.live), [hubView]);
  const canonicalKnown = !!hubView;
  const inCanon = (s: LiveSignal) => !canonicalKnown || inCanonicalLiveEvent(s, canonicalLiveKeys);

  // Single live-now query indexed by EVERY canonical identity key. Merged into
  // each signal so all cards for a fixture render identical CURRENT minute/score/
  // logos even when the feed keys the fixture under an alternate id. No per-card
  // network request; deterministic identity (never fuzzy name matching).
  const liveByFixture = useMemo(() => {
    const m = new Map<string, LiveNowFixture>();
    for (const f of (liveNowQ.data || []) as LiveNowFixture[]) {
      const keys = f.keys && f.keys.length ? f.keys : [String(f.fixture_id)];
      for (const k of keys) if (!m.has(k)) m.set(k, f);
    }
    return m;
  }, [liveNowQ.data]);
  const overlayFor = (s: LiveSignal): LiveNowFixture | null => {
    for (const k of fixtureKeys(s)) { const hit = liveByFixture.get(k); if (hit) return hit; }
    return null;
  };

  const markets = useMemo(() => {
    const set = new Map<string, string>();
    rows.forEach((r) => set.set(r.market_code, resolveMarket(r.market_code, lang, r.market_name).label));
    return Array.from(set.entries());
  }, [rows, lang]);

  const textMatch = (country: string | null, league: string | null, home: string, away: string) => {
    if (!q) return true;
    const hay = `${home} ${away} ${normalizeCompetition(country, league).display}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  };
  const signalFilter = (r: LiveSignal) => (market === "ALL" || r.market_code === market) && textMatch(r.country, r.league, r.home_team, r.away_team);

  // Pending ATLAS signals indexed by exact fixture_id (canonical identity — no fuzzy matching).
  const signalsByFixture = useMemo(() => {
    const m = new Map<string, LiveSignal>();
    for (const s of rows) {
      if (lifecycleOf(s.status, s.won) !== "PENDING") continue;
      const cur = m.get(s.fixture_id);
      if (!cur || (s.market_score ?? 0) > (cur.market_score ?? 0)) m.set(s.fixture_id, s);
    }
    return m;
  }, [rows]);

  // LIVE NOW (authoritative feed): all live fixtures, ATLAS-eligible get full treatment.
  const authLiveNow = useMemo(() => {
    const feed = (liveNowQ.data || []) as LiveNowFixture[];
    return feed
      .map((f) => ({ f, signal: signalsByFixture.get(f.fixture_id) || null }))
      .filter(({ f, signal }) => {
        if (market !== "ALL" && !(signal && signal.market_code === market)) return false;
        return textMatch(f.country, f.league, f.home_team, f.away_team);
      });
  }, [liveNowQ.data, signalsByFixture, q, market]);

  // LIVE NOW (fallback, unauth/preview): derived from ATLAS live signals only.
  const signalLiveNow = useMemo(() => {
    const m = new Map<string, LiveSignal>();
    for (const s of rows.filter(signalFilter)) {
      if (!isLiveNow(s)) continue;
      const cur = m.get(s.fixture_id);
      if (!cur || (s.market_score ?? 0) > (cur.market_score ?? 0)) m.set(s.fixture_id, s);
    }
    return Array.from(m.values());
  }, [rows, q, market]);

  const activeSignals = useMemo(() => rows.filter(signalFilter).filter((s) => lifecycleOf(s.status, s.won) === "PENDING").filter(inCanon), [rows, q, market, canonicalLiveKeys, canonicalKnown]);
  const recentlyFinished = useMemo(() => rows.filter(signalFilter)
    .filter((s) => lifecycleOf(s.status, s.won, { resolved_at: s.resolved_at, final_home_goals: s.final_home_goals, final_away_goals: s.final_away_goals }) !== "PENDING")
    .sort((a, b) => new Date(b.resolved_at || b.created_at).getTime() - new Date(a.resolved_at || a.created_at).getTime())
    .slice(0, 12), [rows, q, market]);

  // Tracking pool = live fixtures ATLAS is watching that have NO active signal yet (auth feed only).
  const trackedPool = useMemo(() => (useAuthFeed ? authLiveNow.filter((x) => !x.signal) : []), [useAuthFeed, authLiveNow]);
  const trackedCount = trackedPool.length;
  const hasAny = activeSignals.length + trackedCount + recentlyFinished.length > 0;
  const filterActive = !!q || market !== "ALL";

  // FREE (no LIVE entitlement): premium locked product view — no real score /
  // minute / team / market / signal values are rendered. Only safe teaser count.
  if (!canLive) {
    return (
      <div data-testid="live-page">
        <PageHeader
          title={lang === "tr" ? "Canlı Maç Merkezi" : "Live Match Center"}
          subtitle={lang === "tr" ? "ATLAS'ın canlı maç zekâsı ve anlık sinyalleri." : "ATLAS live match intelligence and real-time signals."}
        />
        <LiveLocked teaserCount={hubView?.liveTeaserCount ?? null} />
      </div>
    );
  }

  return (
    <div data-testid="live-page">
      <PageHeader
        title={lang === "tr" ? "Canlı Maç Merkezi" : "Live Match Center"}
        subtitle={lang === "tr" ? "ATLAS'ın izlediği canlı maçlar, aktif sinyaller ve son biten karşılaşmalar." : "Live matches ATLAS is watching, active signals and recently finished games."}
        live
        actions={
          <button onClick={() => { refetch(); liveNowQ.refetch(); }} data-testid="live-refresh" className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-2 text-xs font-semibold text-muted-foreground transition hover:text-foreground">
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching || liveNowQ.isFetching ? "animate-spin" : ""}`} /> {d.common.refresh}
          </button>
        }
      />

      <div className="mb-5 grid gap-3 sm:grid-cols-3" data-testid="live-monitor-bar">
        <MonitorStat icon={<Zap className="h-4 w-4" />} tone="bg-atlas/10 text-atlas-400" value={fmtNum(activeSignals.length)} label={lang === "tr" ? "Aktif Sinyal" : "Active Signals"} />
        <MonitorStat icon={<SatelliteDish className="h-4 w-4" />} tone="bg-live/10 text-live-400" value={fmtNum(trackedCount)} label={lang === "tr" ? "Takip Edilen Maç" : "Tracked Matches"} />
        <MonitorStat icon={<Trophy className="h-4 w-4" />} tone="bg-gold/10 text-gold-400" value={fmtNum(recentlyFinished.length)} label={lang === "tr" ? "Son Bitenler" : "Recently Finished"} />
      </div>

      <div className="mb-2 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === "tr" ? "Takım / lig ara…" : "Search team / league…"} data-testid="live-search"
            className="w-full rounded-xl border border-white/10 bg-white/[0.03] py-2.5 pl-9 pr-3 text-sm text-foreground outline-none focus:border-atlas/40" />
        </div>
        <select value={market} onChange={(e) => setMarket(e.target.value)} data-testid="live-market-filter"
          className="rounded-xl border border-white/10 bg-navy-800 px-3 py-2.5 text-sm text-foreground outline-none focus:border-atlas/40">
          <option value="ALL">{lang === "tr" ? "Tüm marketler" : "All markets"}</option>
          {markets.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
        </select>
      </div>

      {isLoading ? <LoadingCards count={6} /> : isError ? <ErrorState error={error as any} onRetry={refetch} />
        : !hasAny ? (
          filterActive ? (
            <EmptyState title={lang === "tr" ? "Aramanızla eşleşen maç yok" : "No matches for your search"} hint={lang === "tr" ? "Arama veya market filtresini temizleyin." : "Clear the search or market filter."} icon={<Search className="h-6 w-6" />} />
          ) : (
            <EmptyState title={lang === "tr" ? "Şu anda ATLAS'ın izlediği canlı maç yok" : "No live matches ATLAS is watching right now"} hint={lang === "tr" ? "Yeni canlı değerlendirmeler oluştuğunda burada görünür." : "New live evaluations will appear here as they are created."} icon={<Activity className="h-6 w-6" />} />
          )
        ) : (
          <>
            {/* Active ATLAS Signals — top priority, strong presentation. Real pending signals only. */}
            <SectionHeader icon={<Zap className="h-4 w-4" />} tone="bg-atlas/15 text-atlas-300" title={lang === "tr" ? "Aktif ATLAS Sinyalleri" : "Active ATLAS Signals"} count={activeSignals.length} />
            {activeSignals.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2" data-testid="active-signals-list">
                {activeSignals.map((s, i) => <LiveMatchCard key={`as-${s.id}`} s={s} index={i} currentLive={overlayFor(s)} />)}
              </div>
            ) : (
              <p className="rounded-xl border border-white/8 bg-white/[0.02] p-4 text-sm text-muted-foreground" data-testid="active-signals-empty">
                {lang === "tr" ? "ATLAS şu anda yayın kriterini geçen aktif bir sinyal üretmedi. Sahte sinyal gösterilmez." : "ATLAS has no active signal meeting the publishing criteria right now. No fabricated signals are shown."}
              </p>
            )}

            {/* ATLAS Tracking Pool — matches ATLAS is watching with no signal yet. Neutral, collapsible. */}
            <div className="mb-3 mt-8 flex items-center">
              <button onClick={() => setPoolOpen((v) => !v)} data-testid="tracking-pool-toggle" className="flex items-center gap-2.5 text-left">
                <span className="grid h-7 w-7 place-items-center rounded-lg bg-live/10 text-live-400"><SatelliteDish className="h-4 w-4" /></span>
                <h2 className="text-base font-bold tracking-tight text-foreground md:text-lg">{lang === "tr" ? "ATLAS Takip Havuzu" : "ATLAS Tracking Pool"}</h2>
                <span className="num rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-xs font-semibold text-muted-foreground">{trackedCount}</span>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${poolOpen ? "rotate-180" : ""}`} />
              </button>
            </div>
            <p className="-mt-1 mb-3 text-xs text-muted-foreground">{lang === "tr" ? "ATLAS'ın izlediği fakat henüz sinyal üretmediği canlı maçlar." : "Live matches ATLAS is watching but has not produced a signal for yet."}</p>
            {poolOpen && (
              trackedCount > 0 ? (
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="tracking-pool-grid">
                  {trackedPool.map(({ f }, i) => <LiveNowCard key={`tp-${f.fixture_id}`} f={f} signal={null} index={i} />)}
                </div>
              ) : (
                <p className="rounded-xl border border-white/8 bg-white/[0.02] p-4 text-sm text-muted-foreground" data-testid="tracking-pool-empty">
                  {lang === "tr" ? "Şu anda ATLAS'ın izlediği sinyalsiz canlı maç yok." : "No tracked live matches without a signal right now."}
                </p>
              )
            )}

            {recentlyFinished.length > 0 && (
              <>
                <SectionHeader icon={<Trophy className="h-4 w-4" />} tone="bg-gold/10 text-gold-400" title={lang === "tr" ? "Son Bitenler" : "Recently Finished"} count={recentlyFinished.length} />
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="recently-finished-grid">
                  {recentlyFinished.map((s, i) => <LiveMatchCard key={`rf-${s.id}`} s={s} index={i} currentLive={overlayFor(s)} />)}
                </div>
              </>
            )}
          </>
        )}

      <div className="mt-8 flex items-start gap-3 rounded-xl border border-white/8 bg-white/[0.02] p-4" data-testid="live-fixture-note">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <p className="text-xs text-muted-foreground">
          {useAuthFeed
            ? (lang === "tr" ? "Canlı maçlar gerçek zamanlı sağlayıcı verisinden alınır; ATLAS değerlendirdiği maçlarda tam zekâ sunar." : "Live matches come from the real-time provider feed; ATLAS adds full intelligence on matches it evaluates.")
            : d.live.fixturesUnavailableHint}
        </p>
      </div>
    </div>
  );
}
